import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ru } from "date-fns/locale";
import { createIssue, updateIssue, postIssue, unpostIssue, deleteIssue, getIssues } from "../../../api/warehouseIssues";
import { getProducts, ProductDto } from "../../../api/products";
import { useScanHandler } from "../../../context/ScannerContext";
import "../../../styles/warehouse/receipt-modal.css";
import "../../../styles/warehouse/issue-modal.css";

const REASONS = [
  { value: "damage",    label: "Порча товара" },
  { value: "expired",   label: "Истёк срок годности" },
  { value: "theft",     label: "Кража" },
  { value: "defect",    label: "Производственный брак" },
  { value: "loss",      label: "Потеря / недостача" },
  { value: "writeoff",  label: "Плановое списание" },
  { value: "error",     label: "Ошибка учёта" },
  { value: "other",     label: "Прочее" },
];

interface ItemRow {
  uid: number;
  productId: number;
  product: any;
  quantity: number;
}

interface Props {
  id: number | null;        // null = новый
  onClose: () => void;
  onSaved: () => void;
}

let uidSeq = 1;

export default function IssueModal({ id, onClose, onSaved }: Props) {
  const [date, setDate] = useState<Date>(new Date());
  const [reason, setReason] = useState("damage");
  const [comment, setComment] = useState("");
  const [items, setItems] = useState<ItemRow[]>([]);
  const [posted, setPosted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState<ProductDto[]>([]);
  const [term, setTerm] = useState("");
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const addRef = useRef<HTMLInputElement>(null);
  const savedId = useRef<number | null>(null);

  useEffect(() => {
    getProducts().then((all) => setCatalog((all ?? []).filter((p) => p.type !== "service"))).catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    getIssues().then((all) => {
      const doc = all.find((d) => d.id === id);
      if (!doc) return;
      setDate(new Date(doc.date || doc.createdAt));
      setReason(doc.reason ?? "damage");
      setComment(doc.comment ?? "");
      setPosted(doc.posted);
      setItems(doc.items.map((i) => ({ uid: uidSeq++, productId: i.product.id, product: i.product, quantity: Number(i.quantity) })));
    });
  }, [id]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  const hay = (p: ProductDto) => `${p.nameRu} ${p.sku ?? ""} ${p.barcode ?? ""} ${p.brand ?? ""} ${p.model ?? ""}`.toLowerCase();
  const suggestions = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (t.length < 2) return [];
    const parts = t.split(/\s+/).filter(Boolean);
    return catalog.filter((p) => parts.every((x) => hay(p).includes(x))).slice(0, 8);
  }, [term, catalog]);

  function addProduct(p: ProductDto) {
    if (posted) return;
    setItems((prev) => {
      const i = prev.findIndex((r) => r.productId === p.id);
      if (i >= 0) {
        const n = prev.slice();
        n[i] = { ...n[i], quantity: n[i].quantity + 1 };
        return n;
      }
      return [...prev, { uid: uidSeq++, productId: p.id, product: p, quantity: 1 }];
    });
    setFlash({ ok: true, text: `Добавлено: ${p.nameRu}` });
    setTerm("");
  }

  // Enter и код со сканера: точное совпадение по ШК/артикулу или единственный результат добавляем сразу
  function addByTerm(raw: string) {
    const t = raw.trim();
    if (!t || posted) return;
    const low = t.toLowerCase();
    const exact = catalog.find((p) => (p.barcode ?? "").toLowerCase() === low || (p.sku ?? "").toLowerCase() === low || (p.externalSkus ?? []).some((s) => s.toLowerCase() === low));
    if (exact) { addProduct(exact); return; }
    const parts = low.split(/\s+/).filter(Boolean);
    const found = catalog.filter((p) => parts.every((x) => hay(p).includes(x)));
    if (found.length === 1) { addProduct(found[0]); return; }
    setTerm(t);
    setFlash({ ok: false, text: found.length ? `Найдено ${found.length}: выберите из списка` : `Товар «${t}» не найден` });
  }

  useScanHandler((code) => addByTerm(code));

  const updateItem = (uid: number, patch: Partial<ItemRow>) => setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  const removeItem = (uid: number) => setItems((prev) => prev.filter((it) => it.uid !== uid));

  const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);

  const save = async (andPost = false) => {
    if (!items.length) return alert("Добавьте хотя бы один товар");
    const bad = items.find((i) => !(Number(i.quantity) >= 1));
    if (bad) return alert(`«${bad.product?.nameRu ?? "Позиция"}»: количество должно быть не меньше 1`);
    setLoading(true);
    try {
      const dto = {
        date: date.toISOString().slice(0, 10),
        reason,
        comment: comment || undefined,
        items: items.map((i) => ({ productId: i.productId, quantity: i.quantity, price: 0 })),
      };
      const cur = savedId.current ?? id;
      const docId = cur ? (await updateIssue(cur, dto)).id : (await createIssue(dto)).id;
      savedId.current = docId; // если проведение не прошло, повторное сохранение правит этот же черновик
      if (andPost) await postIssue(docId);
      onSaved();
      onClose();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      alert(Array.isArray(m) ? m.join("\n") : m || "Ошибка сохранения");
      onSaved();
    } finally {
      setLoading(false);
    }
  };

  const handleUnpost = async () => {
    if (!id) return;
    setLoading(true);
    try { await unpostIssue(id); onSaved(); onClose(); }
    catch (e: any) { alert(e?.response?.data?.message ?? "Ошибка"); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Удалить это списание?")) return;
    setLoading(true);
    try { await deleteIssue(id); onSaved(); onClose(); }
    catch (e: any) { alert(e?.response?.data?.message ?? "Ошибка удаления"); }
    finally { setLoading(false); }
  };

  const reasonLabel = REASONS.find((r) => r.value === reason)?.label ?? reason;
  const focusAdd = () => addRef.current?.focus();
  const onCellEnter = (e: React.KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); focusAdd(); } };

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-window" style={{ maxWidth: 900, width: "96vw" }}>

        <div className="modal-header">
          <div className="modal-title">
            {id ? `Списание №${id}` : "Новое списание"}
            {posted && <span className="issue-badge-posted">Проведён</span>}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <label>Дата документа</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <DatePicker selected={date} onChange={(d: Date | null) => d && setDate(d)} dateFormat="dd.MM.yyyy" locale={ru} className="primary-input" disabled={posted} />
              {!posted && <>
                <button className="secondary-btn" onClick={() => setDate(new Date())}>Сегодня</button>
                <button className="secondary-btn" onClick={() => { const y = new Date(); y.setDate(y.getDate() - 1); setDate(y); }}>Вчера</button>
              </>}
            </div>
          </div>

          <div className="form-row">
            <label>Причина списания</label>
            <select className="primary-input" value={reason} onChange={(e) => setReason(e.target.value)} disabled={posted} style={{ width: "100%" }}>
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div className="form-row">
            <label>Комментарий</label>
            <textarea className="primary-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Дополнительные сведения..." rows={2} disabled={posted} style={{ width: "100%", resize: "vertical", boxSizing: "border-box" }} />
          </div>

          <div className="form-row">
            <label>Товары ({items.length} позиций, {totalQty} ед.)</label>
          </div>

          {!posted && (
            <div className="rc-add" style={{ position: "relative" }}>
              <input
                ref={addRef}
                className="input"
                placeholder="Штрихкод, артикул или название: Enter добавит товар в таблицу"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addByTerm(term); } }}
                autoComplete="off"
              />
              {suggestions.length > 0 && (
                <div className="product-select-dropdown" style={{ position: "absolute", zIndex: 9999, left: 0, right: 0, top: "100%", maxHeight: 240, overflowY: "auto" }}>
                  {suggestions.map((p) => (
                    <div key={p.id} className="product-select-item" onMouseDown={(e) => { e.preventDefault(); addProduct(p); focusAdd(); }}>
                      <span>{p.nameRu}</span>
                      <span style={{ color: "#6b7280", fontSize: 11, marginLeft: 6 }}>{[p.sku, p.barcode && `ШК ${p.barcode}`].filter(Boolean).join(" · ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {flash && <div className={`rc-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}

          <div className="receipt-items-desktop">
            <table className="rc-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>№</th>
                  <th>Товар</th>
                  <th style={{ width: 150 }}>ШК</th>
                  <th style={{ width: 90 }}>Кол-во</th>
                  {!posted && <th style={{ width: 30 }}></th>}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5} style={{ color: "#6b7280", textAlign: "center", padding: 18 }}>Отсканируйте штрихкод или найдите товар выше</td></tr>
                )}
                {items.map((it, idx) => (
                  <tr key={it.uid}>
                    <td>{idx + 1}</td>
                    <td className="rc-name">{it.product?.nameRu}<small>{it.product?.sku}</small></td>
                    <td style={{ fontSize: 12, color: "#9ca3af" }}>{it.product?.barcode || "—"}</td>
                    <td className="num">
                      <input type="number" min={1} value={it.quantity} disabled={posted} onFocus={(e) => e.target.select()} onChange={(e) => updateItem(it.uid, { quantity: Math.floor(+e.target.value) })} onKeyDown={onCellEnter} />
                    </td>
                    {!posted && <td><button className="table-btn" onClick={() => removeItem(it.uid)} title="Убрать">×</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="receipt-items-mobile">
            {items.map((it, idx) => (
              <div key={it.uid} className="receipt-item-card">
                <div className="receipt-item-card-header">
                  <span className="receipt-item-num">{idx + 1}. {it.product?.nameRu}</span>
                  {!posted && <button className="receipt-item-remove" onClick={() => removeItem(it.uid)}>✕</button>}
                </div>
                <div className="receipt-item-row">
                  <div className="receipt-item-field">
                    <label>Кол-во</label>
                    <input type="number" min={1} value={it.quantity} disabled={posted} onChange={(e) => updateItem(it.uid, { quantity: Math.floor(+e.target.value) })} />
                  </div>
                  <div className="receipt-item-field">
                    <label>ШК</label>
                    <div className="receipt-item-sum">{it.product?.barcode || "—"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {posted && (
            <div className="issue-reason-badge">
              <span className="issue-reason-icon">📋</span>
              <span>{reasonLabel}</span>
              {comment && <span className="issue-reason-comment">— {comment}</span>}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {posted ? (
            <>
              <button className="secondary-btn" onClick={onClose}>Закрыть</button>
              <button className="secondary-btn danger-btn" onClick={handleUnpost} disabled={loading}>Отменить проводку</button>
            </>
          ) : (
            <>
              {id && (
                <button className="secondary-btn danger-btn" onClick={handleDelete} disabled={loading} style={{ marginRight: "auto" }}>Удалить</button>
              )}
              <button className="secondary-btn" onClick={() => save(false)} disabled={loading}>Сохранить черновик</button>
              <button className="primary-btn" onClick={() => save(true)} disabled={loading}>{loading ? "Сохранение..." : "Провести списание"}</button>
            </>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
